import React, { useEffect, useState } from 'react'

/* Atoms */
import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Link from 'components_new/atoms/Link'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

/* Molecules */
import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import GenericListItem from 'components_new/molecules/GenericListItem'
import { Option } from 'components_new/molecules/SelectSearch'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

/* Actions */
import { useCustomerAgreementActions } from 'redux/actions/internal/CustomerAgreements'
import { useInternalCustomerActions } from 'redux/actions/internal/Customers'

/* Hooks */
import { useCustomerAgreements } from 'redux/hooks/CustomerAgreements'
import { useTranslation } from 'language/translator'

/* Types */
import {
  InternalCustomer,
  PatchInternalCustomerBody
} from 'types/GlobalCustomer'
import {
  CustomerAgreement,
  CustomerAgreementType,
  PatchCustomerAgreementBody,
  PostCustomerAgreementBody,
  translateCustomerAgreementType
} from 'types/GlobalCustomerAgreement'

interface FeedProps {
  customer: InternalCustomer
}

type ContactPersonProperty =
  | 'agreement_owner_name'
  | 'agreement_owner_email'
  | 'agreement_owner_phone'
  | 'system_owner_name'
  | 'system_owner_email'
  | 'system_owner_phone'
  | 'pub_owner_name'
  | 'pub_owner_email'
  | 'pub_owner_phone'

const Feed = (props: FeedProps) => {
  const {
    tryCreateCustomerAgreement,
    tryGetCustomerAgreements,
    tryUpdatCustomerAgreement,
    tryDeleteCustomerAgreement
  } = useCustomerAgreementActions()

  const { customer } = props
  const [createAgreement, setCreateAgreement] = useState(false)
  const [showAllAgreements, setShowAllAgreements] = useState(false)

  const { tryUpdateCustomer } = useInternalCustomerActions()
  const agreements = useCustomerAgreements(props.customer.id)

  useEffect(() => {
    if (!agreements.fetched) {
      tryGetCustomerAgreements(props.customer.id)
    }
  }, [])

  /*-- agreement field dialog --*/
  const [agreementDialog, setAgreementDialog] = useState<{
    open: boolean
    title: string
    type: TInputType
    options: Option[]
    data: {
      agreementId: string
      property: keyof PatchCustomerAgreementBody
      value: string
    }
    required?: boolean
  }>({
    open: false,
    title: '',
    type: 'text',
    data: { agreementId: '', property: 'type', value: '' },
    options: []
  })

  const handleOpenAgreementDialog = (
    agreement: CustomerAgreement,
    property: keyof PatchCustomerAgreementBody
  ) => {
    let title = ''
    let value = ''
    let type: TInputType = 'text'
    let options: Option[] = []
    let required: boolean | undefined

    if (property === 'type') {
      title = 'Avtalstyp'
      value = agreement.type
      type = 'select'
      options = Object.values(CustomerAgreementType).map((opt) => ({
        title: translateCustomerAgreementType[opt].display,
        id: opt
      }))
      required = true
    } else if (property === 'signed_date') {
      title = 'Signeringsdatum'
      value = agreement.signed_date.split('T')[0]
      type = 'date'
      required = true
    } else if (property === 'termination_date') {
      title = 'Uppsägningsdatum'
      value = agreement.termination_date
        ? agreement.termination_date.split('T')[0]
        : ''
      type = 'date'
    } else if (property === 'start_date') {
      title = 'Startdatum'
      value = agreement.start_date.split('T')[0]
      type = 'date'
      required = true
    } else if (property === 'end_date') {
      title = 'Bekräftat slutdatum'
      value = agreement.end_date ? agreement.end_date.split('T')[0] : ''
      type = 'date'
    } else if (property === 'arr') {
      title = 'ARR'
      value = agreement.arr.toString()
      type = 'number'
      required = true
    }

    setAgreementDialog({
      open: true,
      title,
      type,
      data: { agreementId: agreement.id, property, value },
      options,
      required
    })
  }

  const handleCloseAgreementDialog = () => {
    setAgreementDialog((prev) => ({ ...prev, open: false }))
  }

  const handleEditAgreementDialogValue = (value: string) => {
    setAgreementDialog((prev) => ({
      ...prev,
      data: { ...prev.data, value }
    }))
  }

  const handleSubmitAgreementDialog = () => {
    const { agreementId, property, value } = agreementDialog.data

    let parsedValue: string | number | null = value

    if (property === 'arr') {
      parsedValue = parseInt(value, 10)
    } else if (
      (property === 'termination_date' || property === 'end_date') &&
      value === ''
    ) {
      parsedValue = null
    }

    tryUpdatCustomerAgreement(customer.id, agreementId, {
      [property]: parsedValue
    })

    handleCloseAgreementDialog()
  }

  const formatDate = (date: string | null) => {
    if (!date) return null

    return date.split('T')[0]
  }

  /*-- contact person dialog --*/
  const [contactDialog, setContactDialog] = useState<{
    open: boolean
    title: string
    data: {
      property: ContactPersonProperty
      value: string
    }
  }>({
    open: false,
    title: '',
    data: { property: 'agreement_owner_name', value: '' }
  })

  const contactPersonTitles: Record<string, string> = {
    name: 'Namn',
    email: 'E-post',
    phone: 'Telefon'
  }

  const handleOpenContactDialog = (
    property: ContactPersonProperty,
    value: string | null
  ) => {
    const field = property.split('_').pop() as string

    setContactDialog({
      open: true,
      title: contactPersonTitles[field],
      data: { property, value: value ?? '' }
    })
  }

  const handleCloseContactDialog = () => {
    setContactDialog((prev) => ({ ...prev, open: false }))
  }

  const handleEditContactDialogValue = (value: string) => {
    setContactDialog((prev) => ({
      ...prev,
      data: { ...prev.data, value }
    }))
  }

  const handleSubmitContactDialog = () => {
    const { property, value } = contactDialog.data

    tryUpdateCustomer(customer.id, {
      data: { [property]: value || null }
    } as PatchInternalCustomerBody)

    handleCloseContactDialog()
  }

  return (
    <Box>
      <Avatar customer={customer} />
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Text variant="h6" sx={{ mb: 2 }}>
        Avtal
      </Text>
      {(() => {
        const sorted = [...agreements.data].sort(
          (o1, o2) =>
            new Date(o1.signed_date).getTime() -
            new Date(o2.signed_date).getTime()
        )
        const visible = showAllAgreements ? sorted : sorted.slice(0, 1)

        return visible.map((agreement) => (
          <SettingsGroup
            key={agreement.id}
            title={`${agreement.start_date.split('T')[0]} - ${
              agreement.end_date
                ? agreement.end_date.split('T')[0]
                : 'Tillsvidare'
            }`}
          >
            <SettingsItem
              onClick={() => handleOpenAgreementDialog(agreement, 'type')}
              title={'Avtalstyp'}
              value={translateCustomerAgreementType[agreement.type].display}
              variant="edit"
            />
            <SettingsItem
              onClick={() =>
                handleOpenAgreementDialog(agreement, 'signed_date')
              }
              title={'Signeringsdatum'}
              value={formatDate(agreement.signed_date)}
              variant="edit"
            />
            <SettingsItem
              onClick={() =>
                handleOpenAgreementDialog(agreement, 'termination_date')
              }
              title={'Uppsägningsdatum'}
              value={formatDate(agreement.termination_date)}
              variant="edit"
            />
            <SettingsItem
              onClick={() => handleOpenAgreementDialog(agreement, 'start_date')}
              title={'Startdatum'}
              value={formatDate(agreement.start_date)}
              variant="edit"
            />
            <SettingsItem
              onClick={() => handleOpenAgreementDialog(agreement, 'end_date')}
              title={'Bekräftat slutdatum'}
              value={formatDate(agreement.end_date)}
              variant="edit"
            />
            <SettingsItem
              onClick={() => handleOpenAgreementDialog(agreement, 'arr')}
              title={'ARR'}
              value={`${agreement.arr} kr`}
              variant="edit"
            />
            <SettingsItem
              onClick={() =>
                tryDeleteCustomerAgreement(customer.id, agreement.id)
              }
              title={'Ta bort avtal'}
              color="error"
              variant="action"
            />
          </SettingsGroup>
        ))
      })()}
      <SettingsGroup>
        <SettingsItem
          onClick={() => setCreateAgreement(true)}
          title={'Lägg till avtal'}
          color="success"
          variant="add"
        />
      </SettingsGroup>
      {agreements.data.length > 1 ? (
        <Button
          variant="text"
          size="small"
          onClick={() => setShowAllAgreements((prev) => !prev)}
          sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
        >
          {showAllAgreements
            ? 'Visa färre'
            : `Visa alla (${agreements.data.length})`}
        </Button>
      ) : null}
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Text variant="h6" sx={{ mb: 2 }}>
        Kontaktuppgifter
      </Text>
      <SettingsGroup title={'Avtalsansvarig'}>
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'agreement_owner_name',
              customer.contact_persons.agreement_owner.name
            )
          }
          title={'Namn'}
          value={customer.contact_persons.agreement_owner.name}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'agreement_owner_email',
              customer.contact_persons.agreement_owner.email
            )
          }
          title={'E-post'}
          value={customer.contact_persons.agreement_owner.email}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'agreement_owner_phone',
              customer.contact_persons.agreement_owner.phone
            )
          }
          title={'Telefon'}
          value={customer.contact_persons.agreement_owner.phone}
          variant="edit"
        />
      </SettingsGroup>
      <SettingsGroup title={'Systemansvarig'}>
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'system_owner_name',
              customer.contact_persons.system_owner.name
            )
          }
          title={'Namn'}
          value={customer.contact_persons.system_owner.name}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'system_owner_email',
              customer.contact_persons.system_owner.email
            )
          }
          title={'E-post'}
          value={customer.contact_persons.system_owner.email}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'system_owner_phone',
              customer.contact_persons.system_owner.phone
            )
          }
          title={'Telefon'}
          value={customer.contact_persons.system_owner.phone}
          variant="edit"
        />
      </SettingsGroup>
      <SettingsGroup title={'PUB-ansvarig'}>
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'pub_owner_name',
              customer.contact_persons.pub_owner.name
            )
          }
          title={'Namn'}
          value={customer.contact_persons.pub_owner.name}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'pub_owner_email',
              customer.contact_persons.pub_owner.email
            )
          }
          title={'E-post'}
          value={customer.contact_persons.pub_owner.email}
          variant="edit"
        />
        <SettingsItem
          onClick={() =>
            handleOpenContactDialog(
              'pub_owner_phone',
              customer.contact_persons.pub_owner.phone
            )
          }
          title={'Telefon'}
          value={customer.contact_persons.pub_owner.phone}
          variant="edit"
        />
      </SettingsGroup>

      <CreateAgreementDialog
        open={createAgreement}
        onClose={() => setCreateAgreement(false)}
        onCreate={(body: PostCustomerAgreementBody) =>
          tryCreateCustomerAgreement(props.customer.id, body)
        }
      />

      <SettingsEditDialog
        onChange={handleEditAgreementDialogValue}
        onClose={handleCloseAgreementDialog}
        onSubmit={handleSubmitAgreementDialog}
        open={agreementDialog.open}
        title={agreementDialog.title}
        type={agreementDialog.type}
        value={agreementDialog.data.value}
        options={agreementDialog.options}
        required={agreementDialog.required}
      />

      <SettingsEditDialog
        onChange={handleEditContactDialogValue}
        onClose={handleCloseContactDialog}
        onSubmit={handleSubmitContactDialog}
        open={contactDialog.open}
        title={contactDialog.title}
        type={'text'}
        value={contactDialog.data.value}
      />
    </Box>
  )
}

interface CreateAgreementDialogProps {
  onClose: () => void
  open: boolean
  onCreate: (body: PostCustomerAgreementBody) => void
}

const CreateAgreementDialog = (props: CreateAgreementDialogProps) => {
  const { onClose, open, onCreate } = props
  const { t } = useTranslation()

  const [type, setType] = useState(CustomerAgreementType.Standard)
  const [signedDate, setSignedDate] = useState('')
  const [terminationDate, setTerminationDate] = useState<string | null>(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState<string | null>(null)
  const [arr, setArr] = useState('0')

  useEffect(() => {
    if (open) {
      setType(CustomerAgreementType.Standard)
      setSignedDate(new Date().toLocaleDateString('sv-SE'))
      setTerminationDate(null)
      setStartDate(new Date().toLocaleDateString('sv-SE'))
      setEndDate(null)
      setArr('0')
    }
  }, [open])

  return (
    <AdvancedDialog
      title={'Skapa avtal'}
      open={open}
      onClose={onClose}
      actions={[
        <Button variant="text" onClick={onClose} key="close">
          {t('global', 'close')}
        </Button>,
        <Button
          onClick={() => {
            onCreate({
              type,
              signed_date: signedDate,
              termination_date: terminationDate,
              start_date: startDate,
              end_date: endDate,
              arr: parseInt(arr, 10)
            })

            onClose()
          }}
          key="create"
          disabled={signedDate === '' || startDate === '' || arr === ''}
        >
          {'Skapa'}
        </Button>
      ]}
    >
      <Alert sx={{ mb: 2 }} color="info">
        OBS! Det är viktigt att alla fält fylls i korrekt och ej överlappande
        med andra avtal på kunden.
      </Alert>
      <TextField
        label={'Avtalstyp'}
        select
        onChange={(e) => setType(e.target.value as CustomerAgreementType)}
        size={'small'}
        value={type}
        required
      >
        {Object.values(CustomerAgreementType).map((opt) => {
          return (
            <MenuItem value={opt} key={opt}>
              {translateCustomerAgreementType[opt].display}
            </MenuItem>
          )
        })}
      </TextField>
      <TextField
        label={'Signeringsdatum'}
        onChange={(e) => setSignedDate(e.target.value)}
        size={'small'}
        value={signedDate}
        type={'date'}
        required
      ></TextField>
      <TextField
        label={'Uppsägningsdatum'}
        onChange={(e) => setTerminationDate(e.target.value)}
        size={'small'}
        value={terminationDate || ' '}
        type={'date'}
        helperText={'Sätts vid churn eller omförhandling till nytt avtal.'}
      ></TextField>
      <TextField
        label={'Startdatum'}
        onChange={(e) => setStartDate(e.target.value)}
        size={'small'}
        value={startDate}
        type={'date'}
        required
      ></TextField>
      <TextField
        label={'Bekräftat slutdatum'}
        onChange={(e) => setEndDate(e.target.value)}
        size={'small'}
        value={endDate || ' '}
        type={'date'}
        helperText={'Sätts vid churn eller omförhandling till nytt avtal.'}
      ></TextField>
      <TextField
        label={'ARR'}
        onChange={(e) => setArr(e.target.value)}
        size={'small'}
        value={arr}
        type={'number'}
        helperText={'0 för piloter.'}
        required
      ></TextField>
    </AdvancedDialog>
  )
}

interface AvatarProps {
  customer: InternalCustomer
}

const Avatar = (props: AvatarProps) => {
  const { customer } = props

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1,
        mt: 2
      }}
    >
      <GenericListItem
        component="div"
        size="large"
        sx={{ p: 0, width: 'auto' }}
        title={customer.name}
        {...(customer.link_to_drive
          ? {
              customTitle: (
                <Link
                  href={customer.link_to_drive}
                  target="_blank"
                  rel="noopener"
                >
                  {customer.name}
                </Link>
              )
            }
          : { title: customer.name })}
      />
      {customer.is_demo ? <Chip label={'Demo'} size="small" /> : null}
    </Box>
  )
}

export default Feed
